import { CUSTOMER_MODE } from "@montana/constants/customer-mode";
import { usePage } from "@montana/containers/page";
import { useRouter } from "@montana/containers/router";
import { useSettings } from "@montana/containers/settings/settings.container";
import { useTracking } from "@montana/containers/tracking";
import { DEFAULT_PAGE_TYPE, PAGE_TYPE_MAP } from "@montana/containers/tracking/tracking.sagas";
import { resolveCurrentUrlLocale } from "@montana/helpers/market.helper";
import { IAutoSuggestion, ICategoryList, ISearchResult } from "@montana/models/generated/cms.interface";
import { getSearch, searchAutoSuggestions, searchCategories } from "@montana/services/search.service";
import { trackVirtualPageView } from "@montana/services/tracking";
import { useEffect, useState } from "react";
import useSWR from "swr"
import { useConnectedServiceFunctions } from "../use-connected-service-functions";

/**
 * Search for content and products
 */
export const useCombinedSearch = (query: string) => {
    const { market, customerMode, } = useSettings();
    const { location: routerLocation } = useRouter();
    const currentUrlLocale = resolveCurrentUrlLocale(routerLocation?.pathname, market?.language);
    const [search] = useConnectedServiceFunctions(getSearch);
    const { trackCustomEvent } = useTracking();
    const { metaTitle, kind } = usePage();
    const [oldQuery, setOldQuery] = useState(query)

    const { data, isValidating } = useSWR<ISearchResult>(
        !!query && currentUrlLocale && market?.marketId ? `search:${query}-${currentUrlLocale}-${customerMode}-${market?.marketId}` : null,
        () => search(query, currentUrlLocale, customerMode as CUSTOMER_MODE, market?.marketId!).then(async res => {
            try {
                if (res === "cancelled") {
                    return;
                } else if (!res.ok) {
                    throw `Search request failed with ${res.status}, ${res.statusText}`;
                }

                const searchResult = await res.json();
                return searchResult;
            } catch (error) {
                console.log(error)
            }
        }));

    const totalResults = (data?.totalContent || 0) + (data?.totalProducts || 0);

    useEffect(() => {
        // Fake page change to allow for Google default search tracking.
        trackVirtualPageView({
            pageUrl: window.location.href,
            pagePath: `${window.location.pathname}?q=${query}`,
            pageTitle: metaTitle,
            pageType: PAGE_TYPE_MAP[kind] || DEFAULT_PAGE_TYPE,
        });

        trackCustomEvent('Search', query, String(totalResults));

        setOldQuery(query);
    }, [data])


    return {
        products: data?.items?.filter(item => item.isProduct),
        content: data?.items?.filter(item => !item.isProduct),
        totalContent: data?.totalContent,
        totalProducts: data?.totalProducts,
        totalResults,
        isSearching: oldQuery !== query || (query && data === undefined) || isValidating,
    };
}

export const useSearchCategories = (query?: string) => {
    const { market, customerMode } = useSettings();
    const { location: routerLocation } = useRouter();
    const currentUrlLocale = resolveCurrentUrlLocale(routerLocation?.pathname, market?.language);
    const [getSearchCategories] = useConnectedServiceFunctions(searchCategories);

    const { data } = useSWR<ICategoryList[]>(
        !!query ? `searchCategories:${query}-${currentUrlLocale}` : null,
        () => getSearchCategories(query!, currentUrlLocale, customerMode).then(async res => {
            try {
                if (res === "cancelled") {
                    return;
                } else if (!res.ok) {
                    throw `Search categories request failed with ${res.status}, ${res.statusText}`;
                }

                const searchResult = await res.json();
                return searchResult;
            } catch (error) {
                console.log(error)
            }
        }));

    return {
        searchCategories: data
    }
}

export const useSearchSuggestions = (query?: string, debounce = 300) => {
    const { market } = useSettings();
    const [debouncedQuery, setDebouncedQuery] = useState<string>()
    const [getAutoSuggestions] = useConnectedServiceFunctions(searchAutoSuggestions);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedQuery(query), query ? debounce : 0)
        return () => clearTimeout(timeout)
    }, [query])


    const { data } = useSWR<IAutoSuggestion>(
        !!debouncedQuery ? `autoSuggestions:${debouncedQuery}-${market?.language}` : null,
        () => getAutoSuggestions(debouncedQuery!, market?.language!).then(async res => {
            try {
                if (res === "cancelled") {
                    return;
                } else if (!res.ok) {
                    throw `Search suggestions request failed with ${res.status}, ${res.statusText}`;
                }

                const searchResult = await res.json();
                return searchResult;
            } catch (error) {
                console.log(error)
            }
        }));

    return {
        searchSuggestions: data
    }
}
